import type { ReviewSelectionFilters, ReviewSelectionSortItem } from "@/modules/review-selection/types";

interface ReviewSelectionSummaryProps {
	matchingCount: number;
	activeFilters: ReviewSelectionFilters | null;
	activeSort: ReviewSelectionSortItem[] | null | undefined;
	maxReviews?: number;
	pinnedCount?: number;
	onClick: () => void;
	disabled?: boolean;
}

// Helper to generate filter summary text
function getFilterSummary(filters: ReviewSelectionFilters | null): string {
	if (!filters?.groups) return "No filters";

	const summaryParts: string[] = [];

	// Rating filter
	if (filters.groups.rating) {
		const { value, operator } = filters.groups.rating;
		if (operator === "gte" && typeof value === "number") {
			summaryParts.push(`${value}+ stars`);
		}
	}

	// Type filter
	if (filters.groups.type) {
		const { value } = filters.groups.type;
		if (typeof value === "string") {
			summaryParts.push(value.charAt(0).toUpperCase() + value.slice(1));
		}
	}

	// Content type filter
	if (filters.groups.content_type) {
		const { value } = filters.groups.content_type;
		if (typeof value === "string") {
			const types = value.split(",").map((t) => t.trim().charAt(0).toUpperCase() + t.trim().slice(1));
			summaryParts.push(types.join(", "));
		}
	}

	// Source filter
	if (filters.groups.source) {
		const { value } = filters.groups.source;
		if (typeof value === "string") {
			const sources = value.split(",");
			if (sources.length === 1) {
				summaryParts.push(sources[0].charAt(0).toUpperCase() + sources[0].slice(1).replace(/_/g, " "));
			} else {
				summaryParts.push(`${sources.length} sources`);
			}
		}
	}

	// Date filter
	if (filters.groups.date) {
		const { operator, value } = filters.groups.date;
		if (typeof value === "string") {
			if (operator === "after") {
				summaryParts.push("Recent");
			} else if (operator === "between") {
				summaryParts.push("Date range");
			}
		}
	}

	// Tags filter
	if (filters.groups.tags) {
		const { value } = filters.groups.tags;
		if (typeof value === "string") {
			const tagCount = value.split(",").length;
			summaryParts.push(`${tagCount} tag${tagCount > 1 ? "s" : ""}`);
		}
	}

	return summaryParts.length > 0 ? summaryParts.join(", ") : "No filters";
}

// Helper to generate sort summary text
function getSortSummary(sort: ReviewSelectionSortItem[] | null): string {
	if (!sort || sort.length === 0) return "No sorting";

	const sortParts: string[] = [];

	for (const item of sort) {
		const { field, direction } = item;
		switch (field) {
			case "date":
				sortParts.push(direction === "desc" ? "Newest first" : "Oldest first");
				break;
			case "rating":
				sortParts.push(direction === "desc" ? "Highest rating first" : "Lowest rating first");
				break;
			case "content_type":
				sortParts.push(direction === "asc" ? "Video first" : "Text first");
				break;
			default:
				break;
		}
	}

	return sortParts.length > 0 ? sortParts.join(", ") : "No sorting";
}

export default function ReviewSelectionSummary({ matchingCount, activeFilters, activeSort, maxReviews, pinnedCount = 0, onClick, disabled = false }: ReviewSelectionSummaryProps) {
	const filterSummary = getFilterSummary(activeFilters);
	const sortSummary = getSortSummary(activeSort ?? null);
	const visibleCount = maxReviews != null ? Math.min(matchingCount, Math.max(maxReviews - pinnedCount, 0)) : matchingCount;

	return (
		<button
			className={`w-full p-4 rounded-xl border border-gray-200 text-left transition-all ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-gray-300 hover:bg-gray-50 cursor-pointer"}`}
			onClick={onClick}
			disabled={disabled}
		>
			{/* Counts row */}
			<div className="flex items-center gap-2 mb-2">
				<span className="text-sm font-medium text-gray-900">
					{matchingCount} matched
					<span className="text-tertiary font-normal"> · {visibleCount} visible</span>
				</span>
			</div>

			{/* Helper text and action row */}
			<div className="flex flex-col gap-2">
				<span className="text-sm text-tertiary truncate pr-2">Filter: {filterSummary}</span>
				<span className="text-sm text-tertiary truncate pr-2">Sort: {sortSummary}</span>
				<span className="text-sm font-medium text-gray-600 flex items-center gap-1 shrink-0">
					Select
					<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
					</svg>
				</span>
			</div>
		</button>
	);
}
